import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, UserCheck, UserX, Crown, Users, Trophy } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    }
  };

  useEffect(() => { if (user?.role === "super_admin") load(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const patch = async (uid, body, label) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${uid}`, body);
      toast.success(label || "Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const pending = users.filter(u => u.role === "organizer" && u.organizer_status === "pending");
  const organizers = users.filter(u => u.role === "organizer" && u.organizer_status !== "pending");
  const participants = users.filter(u => u.role === "participant");
  const admins = users.filter(u => u.role === "super_admin");

  return (
    <div className="min-h-screen bg-slate-950">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-9 h-9 text-amber-400" />
          <div>
            <div className="text-xs text-slate-500 font-mono-data tracking-widest">SUPER ADMIN</div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-50">User Management</h1>
          </div>
        </div>
        <p className="text-slate-400 mt-3">Approve organizer signups, change roles, and see every registered user in one place.</p>

        {/* Purge test accounts */}
        <section className="mt-8 rounded-2xl border border-rose-500/20 bg-rose-500/5 p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="purge-section">
          <div>
            <div className="text-rose-300 font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 opacity-0" /> Test-data cleanup</div>
            <div className="text-slate-400 text-sm mt-1">Removes every super-admin account except the seeded owner, plus their tournaments and sessions. Useful after a QA cycle.</div>
          </div>
          <Button
            variant="outline"
            className="border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/15"
            onClick={async () => {
              if (!window.confirm("Purge stray super-admin accounts created by QA runs? Anthony's account is preserved.")) return;
              setBusy(true);
              try {
                const r = await api.post("/admin/purge-test-accounts");
                const d = r.data;
                toast.success(`Removed ${d.stray_admins_removed} admin(s), ${d.their_tournaments_removed} tournament(s), ${d.their_sessions_removed + d.orphan_sessions_removed} session(s)`);
                load();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Purge failed");
              } finally {
                setBusy(false);
              }
            }}
            data-testid="purge-test-accounts"
          >
            Purge Test Accounts
          </Button>
        </section>

        {/* Pending approvals */}
        <section className="mt-10">
          <div className="flex items-baseline gap-3">
            <h2 className="text-2xl text-slate-50">Pending organizer approvals</h2>
            <span className="text-amber-400 font-mono-data text-sm">({pending.length})</span>
          </div>
          {pending.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-white/10 p-8 text-center text-slate-500">All caught up.</div>
          ) : (
            <div className="mt-4 space-y-2">
              {pending.map(u => (
                <UserRow key={u.user_id} u={u} busy={busy}
                  actions={
                    <>
                      <Button size="sm" onClick={() => patch(u.user_id, { organizer_status: "approved" }, `${u.name} approved`)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid={`approve-${u.user_id}`}>
                        <UserCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => patch(u.user_id, { organizer_status: "rejected" }, `${u.name} rejected`)} className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10" data-testid={`reject-${u.user_id}`}>
                        <UserX className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  }
                />
              ))}
            </div>
          )}
        </section>

        {/* Approved organizers */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex items-baseline gap-3">
              <h2 className="text-2xl text-slate-50">Organizers</h2>
              <span className="text-slate-500 font-mono-data text-sm">({organizers.length})</span>
            </div>
            {organizers.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete ALL ${organizers.length} organizers and every tournament they organized? Super-admins are unaffected. This can't be undone.`)) return;
                  setBusy(true);
                  try {
                    const r = await api.post("/admin/bulk-delete-users", { role: "organizer" });
                    const d = r.data;
                    toast.success(`Deleted ${d.users_deleted} organizer(s) and ${d.tournaments_deleted} tournament(s)`);
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Bulk delete failed");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 text-xs font-mono-data tracking-widest uppercase"
                data-testid="bulk-delete-organizers"
              >
                Delete All Organizers
              </button>
            )}
          </div>
          <div className="mt-4 space-y-2">
            {organizers.map(u => (
              <UserRow key={u.user_id} u={u} busy={busy}
                actions={
                  <>
                    {u.organizer_status === "rejected" ? (
                      <Button size="sm" onClick={() => patch(u.user_id, { organizer_status: "approved" }, `${u.name} approved`)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid={`approve-${u.user_id}`}>
                        <UserCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => patch(u.user_id, { role: "participant" }, `${u.name} → participant`)} className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10" data-testid={`demote-${u.user_id}`}>
                        Demote to Participant
                      </Button>
                    )}
                  </>
                }
              />
            ))}
            {organizers.length === 0 && <div className="text-slate-500 text-sm">No approved organizers yet.</div>}
          </div>
        </section>

        {/* Participants */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex items-baseline gap-3">
              <h2 className="text-2xl text-slate-50">Participants</h2>
              <span className="text-slate-500 font-mono-data text-sm">({participants.length})</span>
            </div>
            {participants.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete ALL ${participants.length} participants? They will also be removed from any tournament that's still open for signup. This can't be undone.`)) return;
                  setBusy(true);
                  try {
                    const r = await api.post("/admin/bulk-delete-users", { role: "participant" });
                    const d = r.data;
                    toast.success(`Deleted ${d.users_deleted} participant(s); cleaned ${d.open_tournaments_updated} open tournament(s)`);
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Bulk delete failed");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 text-xs font-mono-data tracking-widest uppercase"
                data-testid="bulk-delete-participants"
              >
                Delete All Participants
              </button>
            )}
          </div>
          <div className="mt-4 space-y-2">
            {participants.map(u => (
              <UserRow key={u.user_id} u={u} busy={busy}
                actions={
                  <Button size="sm" onClick={() => patch(u.user_id, { role: "organizer", organizer_status: "approved" }, `${u.name} promoted`)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`promote-${u.user_id}`}>
                    <Crown className="w-4 h-4 mr-1" /> Promote to Organizer
                  </Button>
                }
              />
            ))}
            {participants.length === 0 && <div className="text-slate-500 text-sm">No participants yet.</div>}
          </div>
        </section>

        {/* Super admins */}
        {admins.length > 0 && (
          <section className="mt-10 mb-10">
            <div className="flex items-baseline gap-3">
              <h2 className="text-2xl text-slate-50">Super Admins</h2>
              <span className="text-slate-500 font-mono-data text-sm">({admins.length})</span>
            </div>
            <div className="mt-4 space-y-2">
              {admins.map(u => <UserRow key={u.user_id} u={u} busy={busy} actions={<Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">Locked</Badge>} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function UserRow({ u, actions, busy }) {
  const statusMap = {
    approved: { label: "APPROVED", className: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5" },
    pending: { label: "PENDING", className: "text-amber-300 border-amber-500/30 bg-amber-500/5" },
    rejected: { label: "REJECTED", className: "text-rose-300 border-rose-500/30 bg-rose-500/5" },
  };
  const st = u.organizer_status ? statusMap[u.organizer_status] : null;
  const roleIcon = u.role === "super_admin" ? <ShieldCheck className="w-4 h-4 text-amber-400" /> : u.role === "organizer" ? <Crown className="w-4 h-4 text-amber-300" /> : <Users className="w-4 h-4 text-slate-400" />;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/50 px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`user-row-${u.user_id}`}>
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">{roleIcon}</div>
        <div className="min-w-0">
          <div className="text-slate-100 truncate flex items-center gap-2">
            {u.name}
            {st && <span className={`text-[9px] font-mono-data tracking-widest px-1.5 py-0.5 rounded border uppercase ${st.className}`}>{st.label}</span>}
          </div>
          <div className="text-slate-500 text-xs truncate">{u.email || "—"} · <span className="font-mono-data uppercase tracking-wider">{u.role}</span> · <Trophy className="inline w-3 h-3 -mt-0.5" /> {u.tournaments_organized || 0} organized</div>
        </div>
      </div>
      <div className="flex items-center gap-2" style={{ opacity: busy ? 0.6 : 1 }}>
        {actions}
      </div>
    </div>
  );
}
